import {
  type AutoTargetGroup,
  type CampaignCreationCapabilities,
  type CampaignCreationRequestStatus,
  type CampaignCreationSubmit,
  CampaignCreationSubmitSchema,
  type ManualKeywordMatchType,
  type NegativeMatchType,
  type Placement,
  type ProductAdvertisingEligibility,
  type ProductEligibilityReason,
} from "@adpilot/shared";
import { format } from "date-fns";

export const AUTO_TARGET_GROUPS: AutoTargetGroup[] = [
  "close_match",
  "loose_match",
  "substitutes",
  "complements",
];

export interface SelectedProduct {
  sku: string;
  asin: string;
  variationGroupKey: string;
  title: string | null;
  imageUrl: string | null;
  eligibility?: ProductAdvertisingEligibility;
  eligibilityReasons?: ProductEligibilityReason[];
  eligibilityCheckedAt?: string | null;
  eligibilitySource?: "amazon-ads" | null;
}
export interface NegativeKeywordTarget {
  text: string;
  matchType: NegativeMatchType;
}

export interface ManualKeywordTarget {
  text: string;
  matchType: ManualKeywordMatchType;
  bid: string;
}

export interface ManualProductTarget {
  asin: string;
  bid: string;
}

export interface CampaignCreationFormValues {
  campaignName: string;
  // ADP-137: local portfolio uuid; the backend resolves + validates it.
  portfolioId: string | null;
  adGroupName: string;
  startDate: string;
  endDate: string;
  dailyBudget: string;
  biddingStrategy: string;
  offsiteStrategy: "increase_reach" | "limit_off_amazon_spend";
  placementAdjustments: Partial<Record<Placement, string>>;
  targetingMode: "automatic" | "manual";
  manualTargetingType: "keyword" | "product";
  manualDefaultBid: string;
  manualKeywords: ManualKeywordTarget[];
  manualProducts: ManualProductTarget[];
  bidMode: "default" | "per_target_group";
  defaultBid: string;
  groupEnabled: Record<AutoTargetGroup, boolean>;
  groupBids: Record<AutoTargetGroup, string>;
  products: SelectedProduct[];
  negativeKeywords: NegativeKeywordTarget[];
  negativeProducts: string[];
}

export function createDefaultNames(now = new Date()) {
  const timestamp = format(now, "M/d/yyyy HH:mm:ss.SSS");
  return { campaignName: `Campaign - ${timestamp}`, adGroupName: `Ad Group - ${timestamp}` };
}

export function createInitialCampaignForm(
  capabilities: CampaignCreationCapabilities,
  now = new Date(),
): CampaignCreationFormValues {
  const names = createDefaultNames(now);
  return {
    ...names,
    portfolioId: null,
    startDate: "",
    endDate: "",
    dailyBudget: "",
    biddingStrategy: capabilities.biddingStrategies.includes("LEGACY_FOR_SALES")
      ? "LEGACY_FOR_SALES"
      : capabilities.biddingStrategies.includes("AUTO_FOR_SALES")
        ? "AUTO_FOR_SALES"
        : "",
    offsiteStrategy: "limit_off_amazon_spend",
    placementAdjustments: Object.fromEntries(
      capabilities.placementAdjustment.placements.map((placement) => [placement, "0"]),
    ),
    targetingMode: "automatic",
    manualTargetingType: "keyword",
    manualDefaultBid: "",
    manualKeywords: [],
    manualProducts: [],
    bidMode: "default",
    defaultBid: "",
    groupEnabled: {
      close_match: true,
      loose_match: true,
      substitutes: true,
      complements: true,
    },
    groupBids: {
      close_match: "",
      loose_match: "",
      substitutes: "",
      complements: "",
    },
    products: [],
    negativeKeywords: [],
    negativeProducts: [],
  };
}

function decimalPlaces(value: string): number {
  const decimal = value.split(".")[1];
  return decimal?.length ?? 0;
}

function parseMoney(
  raw: string,
  limits: { min: number; max: number; decimals: number },
  path: string,
): { value: number } | { error: CampaignFormError } {
  if (!raw.trim()) return { error: { path, key: "required" } } as const;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < limits.min || value > limits.max) {
    return { error: { path, key: "range", min: limits.min, max: limits.max } } as const;
  }
  if (decimalPlaces(raw) > limits.decimals) {
    return { error: { path, key: "precision", decimals: limits.decimals } } as const;
  }
  return { value } as const;
}

export interface CampaignFormError {
  path: string;
  key: string;
  min?: number;
  max?: number;
  decimals?: number;
}

function submitIssueToFormPath(path: PropertyKey[], candidate: CampaignCreationSubmit): string {
  const joined = path.join(".");
  const exactPaths: Record<string, string> = {
    "campaign.name": "campaignName",
    "campaign.startDate": "startDate",
    "campaign.endDate": "endDate",
    "campaign.dailyBudget": "dailyBudget",
    "campaign.biddingStrategy": "biddingStrategy",
    "adGroup.name": "adGroupName",
    "adGroup.products": "products",
    "adGroup.autoTargetingBids.defaultBid": "defaultBid",
    "adGroup.manualTargeting.defaultBid": "manualDefaultBid",
    "adGroup.manualTargeting.targets": "manualTargets",
    "negatives.keywords": "negativeKeywords",
    "negatives.productAsins": "negativeProducts",
  };
  const exact = exactPaths[joined];
  if (exact) return exact;
  if (joined.startsWith("adGroup.products.")) return "products";
  if (joined.startsWith("negatives.keywords.")) return "negativeKeywords";
  if (joined.startsWith("negatives.productAsins.")) return "negativeProducts";
  if (joined.startsWith("adGroup.autoTargetingBids.targets.")) {
    return `groupBids.${path[3]?.toString() ?? ""}`;
  }
  if (joined.startsWith("adGroup.manualTargeting.targets.")) return "manualTargets";
  if (path[0] === "campaign" && path[1] === "placementAdjustments") {
    const index = typeof path[2] === "number" ? path[2] : Number(path[2]);
    const placement = candidate.campaign.placementAdjustments[index]?.placement;
    if (placement) return `placementAdjustments.${placement}`;
  }
  return joined;
}

function submitIssueToValidationKey(path: PropertyKey[], message: string): string {
  const joined = path.join(".");
  if (joined === "campaign.startDate" && message === "startDate cannot be in the past") {
    return "startDatePast";
  }
  if (joined === "campaign.endDate" && message === "endDate must be on or after startDate") {
    return "endDateBeforeStart";
  }
  return "invalid";
}

export function buildCampaignCreationSubmit(
  requestId: string,
  values: CampaignCreationFormValues,
  capabilities: CampaignCreationCapabilities,
):
  | { success: true; data: CampaignCreationSubmit }
  | { success: false; errors: CampaignFormError[] } {
  const errors: CampaignFormError[] = [];
  if (!values.campaignName.trim()) errors.push({ path: "campaignName", key: "required" });
  else if (values.campaignName.trim().length > capabilities.campaignName.maxLength) {
    errors.push({
      path: "campaignName",
      key: "maxLength",
      max: capabilities.campaignName.maxLength,
    });
  }
  if (!values.adGroupName.trim()) errors.push({ path: "adGroupName", key: "required" });
  if (!values.startDate) errors.push({ path: "startDate", key: "required" });
  if (!values.biddingStrategy) errors.push({ path: "biddingStrategy", key: "required" });
  if (values.products.length === 0) errors.push({ path: "products", key: "required" });
  if (values.products.length > capabilities.limits.maxProducts) {
    errors.push({ path: "products", key: "limit", max: capabilities.limits.maxProducts });
  }
  if (values.negativeKeywords.length > capabilities.limits.maxNegativeKeywords) {
    errors.push({
      path: "negativeKeywords",
      key: "limit",
      max: capabilities.limits.maxNegativeKeywords,
    });
  }
  if (values.negativeProducts.length > capabilities.limits.maxNegativeProducts) {
    errors.push({
      path: "negativeProducts",
      key: "limit",
      max: capabilities.limits.maxNegativeProducts,
    });
  }
  const dailyBudget = parseMoney(values.dailyBudget, capabilities.dailyBudget, "dailyBudget");
  if ("error" in dailyBudget) errors.push(dailyBudget.error);

  const targetSettings: Partial<Record<AutoTargetGroup, { enabled: boolean; bid: number | null }>> =
    {};
  let defaultBid: number | undefined;
  if (values.targetingMode === "automatic" && values.bidMode === "default") {
    const parsed = parseMoney(values.defaultBid, capabilities.bid, "defaultBid");
    if ("error" in parsed) errors.push(parsed.error);
    else defaultBid = parsed.value;
  } else if (values.targetingMode === "automatic") {
    for (const group of AUTO_TARGET_GROUPS) {
      if (!values.groupEnabled[group]) {
        targetSettings[group] = { enabled: false, bid: null };
        continue;
      }
      const parsed = parseMoney(values.groupBids[group], capabilities.bid, `groupBids.${group}`);
      if ("error" in parsed) errors.push(parsed.error);
      else targetSettings[group] = { enabled: true, bid: parsed.value };
    }
    if (!AUTO_TARGET_GROUPS.some((group) => values.groupEnabled[group])) {
      errors.push({ path: "groupEnabled", key: "autoTargetRequired" });
    }
  }

  let manualDefaultBid: number | undefined;
  const manualKeywordTargets: { text: string; matchType: ManualKeywordMatchType; bid: number }[] =
    [];
  const manualProductTargets: { asin: string; bid: number }[] = [];
  if (values.targetingMode === "manual") {
    const parsedDefault = parseMoney(values.manualDefaultBid, capabilities.bid, "manualDefaultBid");
    if ("error" in parsedDefault) errors.push(parsedDefault.error);
    else manualDefaultBid = parsedDefault.value;

    const activeTargets =
      values.manualTargetingType === "keyword" ? values.manualKeywords : values.manualProducts;
    if (activeTargets.length === 0) errors.push({ path: "manualTargets", key: "required" });
    for (const [index, target] of activeTargets.entries()) {
      const parsedBid = parseMoney(target.bid, capabilities.bid, `manualTargets.${index}.bid`);
      if ("error" in parsedBid) {
        errors.push(parsedBid.error);
        continue;
      }
      if (values.manualTargetingType === "keyword" && "text" in target) {
        manualKeywordTargets.push({
          text: target.text.trim(),
          matchType: target.matchType,
          bid: parsedBid.value,
        });
      } else if ("asin" in target) {
        manualProductTargets.push({ asin: target.asin, bid: parsedBid.value });
      }
    }
  }

  const placementAdjustments = capabilities.placementAdjustment.placements.flatMap((placement) => {
    const raw = values.placementAdjustments[placement] ?? "0";
    const percentage = Number(raw);
    if (
      !Number.isInteger(percentage) ||
      percentage < capabilities.placementAdjustment.min ||
      percentage > capabilities.placementAdjustment.max
    ) {
      errors.push({
        path: `placementAdjustments.${placement}`,
        key: "integerRange",
        min: capabilities.placementAdjustment.min,
        max: capabilities.placementAdjustment.max,
      });
      return [];
    }
    return [{ placement, percentage }];
  });
  if (errors.length > 0 || "error" in dailyBudget) return { success: false, errors };

  const candidate = {
    requestId,
    campaign: {
      name: values.campaignName.trim(),
      portfolioId: values.portfolioId ?? null,
      startDate: values.startDate,
      endDate: values.endDate || null,
      dailyBudget: dailyBudget.value,
      biddingStrategy: values.biddingStrategy,
      ...(capabilities.features.offAmazonSettings === "writable"
        ? {
            offAmazonSettings: {
              expandedAudienceReach: values.offsiteStrategy === "increase_reach",
            },
          }
        : {}),
      placementAdjustments,
    },
    adGroup: {
      name: values.adGroupName.trim(),
      products: values.products.map(({ asin, sku }) => ({ asin, sku })),
      ...(values.targetingMode === "automatic"
        ? {
            targetingType: "automatic" as const,
            autoTargetingBids:
              values.bidMode === "default"
                ? { mode: "default" as const, defaultBid: defaultBid as number }
                : {
                    mode: "per_target_group" as const,
                    targets: {
                      close_match: targetSettings.close_match as {
                        enabled: boolean;
                        bid: number | null;
                      },
                      loose_match: targetSettings.loose_match as {
                        enabled: boolean;
                        bid: number | null;
                      },
                      substitutes: targetSettings.substitutes as {
                        enabled: boolean;
                        bid: number | null;
                      },
                      complements: targetSettings.complements as {
                        enabled: boolean;
                        bid: number | null;
                      },
                    },
                  },
          }
        : {
            targetingType: "manual" as const,
            manualTargeting:
              values.manualTargetingType === "keyword"
                ? {
                    type: "keyword" as const,
                    defaultBid: manualDefaultBid as number,
                    targets: manualKeywordTargets,
                  }
                : {
                    type: "product" as const,
                    defaultBid: manualDefaultBid as number,
                    targets: manualProductTargets,
                  },
          }),
    },
    negatives: {
      keywords: values.negativeKeywords,
      productAsins: values.negativeProducts,
    },
  };
  const parsed = CampaignCreationSubmitSchema.safeParse(candidate);
  if (!parsed.success) {
    return {
      success: false,
      errors: parsed.error.issues.map((issue) => ({
        path: submitIssueToFormPath(issue.path, candidate as CampaignCreationSubmit),
        key: submitIssueToValidationKey(issue.path, issue.message),
      })),
    };
  }
  return { success: true, data: parsed.data };
}

export function parseProductIdentifiers(input: string): string[] {
  const seen = new Set<string>();
  return input
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter((part) => {
      if (!part || seen.has(part)) return false;
      seen.add(part);
      return true;
    });
}

export function parseNegativeKeywords(
  input: string,
  matchTypes: NegativeMatchType[],
  existing: NegativeKeywordTarget[],
) {
  const existingKeys = new Set(
    existing.map((item) => `${item.text.toLocaleLowerCase()}:${item.matchType}`),
  );
  const added: NegativeKeywordTarget[] = [];
  const duplicates: string[] = [];
  const invalid: string[] = [];
  for (const segment of input.split(/[\r\n,]+/)) {
    const text = segment.trim();
    if (!text) continue;
    if (matchTypes.length === 0) {
      invalid.push(text);
      continue;
    }
    for (const matchType of matchTypes) {
      const key = `${text.toLocaleLowerCase()}:${matchType}`;
      if (existingKeys.has(key)) duplicates.push(text);
      else {
        existingKeys.add(key);
        added.push({ text, matchType });
      }
    }
  }
  return { added, duplicates, invalid };
}

const ASIN_PATTERN = /^[A-Z0-9]{10}$/;
export function parseNegativeProducts(input: string, existing: string[]) {
  const seen = new Set(existing.map((asin) => asin.toUpperCase()));
  const added: string[] = [];
  const duplicates: string[] = [];
  const invalid: string[] = [];
  for (const segment of input.split(/[\s,]+/)) {
    const value = segment.trim().toUpperCase();
    if (!value) continue;
    if (!ASIN_PATTERN.test(value)) invalid.push(segment.trim());
    else if (seen.has(value)) duplicates.push(value);
    else {
      seen.add(value);
      added.push(value);
    }
  }
  return { added, duplicates, invalid };
}

export type CampaignCreationResultKind =
  | "processing"
  | "succeeded"
  | "resumable"
  | "paused"
  | "manual_remediation"
  | "edit"
  | "reconcile";

export function classifyCampaignCreationResult(
  status: CampaignCreationRequestStatus,
): CampaignCreationResultKind {
  if (status.status === "pending" || status.status === "processing") return "processing";
  if (status.status === "succeeded" && status.localCampaignId) return "succeeded";
  if (status.recoveryDisposition === "reconcile") return "reconcile";
  if (status.recoveryDisposition === "manual_remediation") return "manual_remediation";
  if (status.recoveryDisposition === "edit" && status.status === "failed") return "edit";
  if (status.recoveryDisposition === "resume" && status.resumableFrom) {
    const step = status.steps.find((candidate) => candidate.step === status.resumableFrom);
    return step?.status === "pending" && !step.errorCode ? "paused" : "resumable";
  }
  return "reconcile";
}

export function hasCampaignCreationAuthorizationFailure(
  status: CampaignCreationRequestStatus,
): boolean {
  return status.steps.some(
    (step) =>
      step.errorCode === "campaign_creation.authorization_invalid" ||
      step.itemFailures.some(
        (failure) => failure.errorCode === "campaign_creation.authorization_invalid",
      ),
  );
}
